import _classCallCheck from 'babel-runtime/helpers/classCallCheck';
import _possibleConstructorReturn from 'babel-runtime/helpers/possibleConstructorReturn';
import _inherits from 'babel-runtime/helpers/inherits';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { MediaPlayerControls } from './MediaPlayerControls';
import { SpeakerAndProgressButton } from './SpeakerAndProgressButton';
import { mediaSelectors } from '../redux/media-reducer';
import { AudioPlayer } from '../utils/AudioPlayer';
import { withAudioParsing } from '../utils/withAudioParsing';

/**
 * AudioWithControls
 *  connects between a UI audio widget and an AudioPlayer
 */

var AudioWithControlsBase = function (_PureComponent) {
  _inherits(AudioWithControlsBase, _PureComponent);

  function AudioWithControlsBase(props) {
    _classCallCheck(this, AudioWithControlsBase);

    var _this = _possibleConstructorReturn(this, _PureComponent.call(this, props));

    _this.handlePlay = function () {
      _this.setState({ played: true });
      if (_this.props.onPlayed) {
        _this.props.onPlayed(_this.audioPlayer.duration, _this.audioPlayer.position);
      }
    };

    _this.handlePause = function () {
      if (_this.props.onPaused) {
        _this.props.onPaused(_this.audioPlayer.position);
      }
    };

    _this.handleEnded = function () {
      if (_this.props.onEnded) {
        _this.props.onEnded();
      }
      _this.audioPlayer.currentIndex = 0;
    };

    _this.togglePlay = function (e) {
      e && e.stopPropagation();
      if (_this.props.disabled || !_this.props.audioUri) {
        return;
      }
      if (_this.audioPlayer.isPlaying) {
        _this.audioPlayer.pause();
      } else {
        if (!_this.audioPlayer.isLoaded) {
          if (_this.props.audioUris) {
            _this.audioPlayer.setAudio(_this.props.audioUris);
          } else {
            _this.audioPlayer.setAudio(_this.props.audioUri);
          }
        }
        _this.audioPlayer.play();
      }
    };

    _this.audioPlayer = new AudioPlayer();
    _this.audioPlayer.onEnd(_this.handleEnded);
    props.onLoadError && _this.audioPlayer.onLoadError(props.onLoadError);
    props.onLoaded && _this.audioPlayer.onLoaded(props.onLoaded);
    _this.audioPlayer.onPause(_this.handlePause);
    _this.audioPlayer.onPlay(_this.handlePlay);
    props.onTimeUpdate && _this.audioPlayer.onTick(props.onTimeUpdate);

    if ((props.preloadAudio || props.withFullControls) && props.audioUri) {
      if (props.audioUris) {
        _this.audioPlayer.setAudio(props.audioUris);
      } else {
        _this.audioPlayer.setAudio(props.audioUri);
      }
    }
    _this.state = {
      played: false
    };
    _this.autoplayTimerId = null;
    return _this;
  }

  AudioWithControlsBase.prototype.componentDidMount = function componentDidMount() {
    var _this2 = this;

    if (this.props.autoplay) {
      this.togglePlay();
    }
    if (this.props.delayedAutoplayMs) {
      this.autoplayTimerId = setTimeout(function () {
        _this2.autoplayTimerId = null;
        _this2.togglePlay();
      }, this.props.delayedAutoplayMs);
    }
  };

  AudioWithControlsBase.prototype.componentDidUpdate = function componentDidUpdate(prevProps) {
    if (this.props.audioUri !== prevProps.audioUri || !!this.props.audioUris !== !!prevProps.audioUris || this.props.audioUris && (this.props.audioUris.length !== prevProps.audioUris.length || this.props.audioUris.some(function (aUri) {
      return !prevProps.audioUris.includes(aUri);
    }))) {
      if (prevProps.audioUri) {
        this.audioPlayer.pause();
      }
      if (this.props.audioUris) {
        this.audioPlayer.setAudio(this.props.audioUris);
      } else if (this.props.audioUri) {
        this.audioPlayer.setAudio(this.props.audioUri);
      }
    }
  };

  AudioWithControlsBase.prototype.componentWillUnmount = function componentWillUnmount() {
    if (this.autoplayTimerId) {
      clearTimeout(this.autoplayTimerId);
    }
    this.audioPlayer.clearMedia();
  };

  AudioWithControlsBase.prototype.render = function render() {
    if (!this.props.audioUri && this.props.withFullControls) {
      return null;
    }
    var isDisabled = !!(this.props.disabled || !this.props.audioUri || this.props.isRecording || this.props.limited && this.state.played);

    if (this.props.withFullControls) {
      return React.createElement(MediaPlayerControls, {
        mediaPlayer: this.audioPlayer,
        disabled: isDisabled,
        withDurationDisp: this.props.withDurationDisp
      });
    } else {
      return React.createElement(SpeakerAndProgressButton, {
        buttonSize: this.props.buttonSize,
        text: this.props.buttonText,
        fill: this.props.fill,
        fillColor: this.props.fillColor,
        darkTheme: this.props.darkTheme,
        mediaPlayer: this.audioPlayer,
        disabled: isDisabled,
        onClick: this.togglePlay
      });
    }
  };

  return AudioWithControlsBase;
}(PureComponent);

AudioWithControlsBase.propTypes = {
  // ** we need one of the following three props for sound to play:
  // src is a URI that needs no processing
  src: PropTypes.string,

  // audios - the RS content type, maybe an array of them
  audios: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),

  // put uris needing signing here, else dont use it
  unsignedUri: PropTypes.string,

  // only button and progress or full controls?
  withFullControls: PropTypes.bool,

  // preloadAudio: should it always load the audio when it is created?
  //  if withFullControls is set it always preloads, so this
  //  prop is aimed at non-full-controls ones
  preloadAudio: PropTypes.bool,

  // autoplay starts playing right away when it mounts
  autoplay: PropTypes.bool,

  // delayedAutoplayMs starts it playing that many ms after it mounts
  delayedAutoplayMs: PropTypes.number,

  // limited means:  it can only be played once (for assessment)
  limited: PropTypes.bool,

  // if it is a simple button and progress, we can specify these things:
  fill: PropTypes.bool,
  fillColor: PropTypes.string,
  buttonText: PropTypes.string,
  buttonSize: PropTypes.number,

  // if it is full controls we can specify this:
  withDurationDisp: PropTypes.bool,

  // this is added by withAudioParsing and is an array of parsed URIs
  //  or null if there was a parsing error OR if there is only one URI
  audioUris: PropTypes.array,
  // the first URI in audioUris, or the only URI (in which case audioUris is null)
  audioUri: PropTypes.string,

  onPlayed: PropTypes.func,
  onPaused: PropTypes.func,
  onEnded: PropTypes.func,

  // darkTheme for SpeakerAndProgressButton
  darkTheme: PropTypes.bool
};

AudioWithControlsBase.defaultProps = {
  buttonSize: 60,
  darkTheme: false
};

var mapStateToProps = function mapStateToProps(state) {
  return {
    isRecording: mediaSelectors.isRecording(state)
  };
};

export var AudioWithControls = connect(mapStateToProps)(withAudioParsing(AudioWithControlsBase));